import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Phone, ClipboardList, CheckCircle, ArrowRight, AlertTriangle } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ServiceGallery } from '../components/ServiceGallery';

export const RoofRepairsPage = () => {
  useEffect(() => {
    // SEO: Set page title and meta description (Priority 8)
    document.title = "Roof Repairs Sydney | Fast and Professional Roofing Repairs | Dezire Metal Roofing";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Need roof repairs in Sydney? Dezire Metal Roofing provides prompt, professional roof repair services for leaks, damage, and general roofing issues.');
    }
    window.scrollTo(0, 0);
  }, []);

  const repairIssues = [
    "Roof leaks",
    "Loose or damaged roofing sheets",
    "Flashing issues",
    "Rust or deterioration",
    "Storm-related damage",
    "Water overflow problems linked to gutters"
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="page-title">
              Roof Repairs <span className="text-primary">Sydney</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              A damaged roof can quickly lead to bigger problems if left untreated. Dezire Metal Roofing provides prompt roof repair services across Greater Sydney, helping homeowners address leaks, damaged sheets, flashing issues, and general wear before they become costly repairs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book-inspection">
                <Button size="lg" className="bg-primary hover:bg-accent text-white" data-testid="hero-cta-btn">
                  <ClipboardList className="w-5 h-5 mr-2" />
                  Book a Free Inspection
                </Button>
              </Link>
              <a href="/call">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
                  <Phone className="w-5 h-5 mr-2" />
                  Call 0433 973 844
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Common Repair Issues We Handle
              </h2>
              <ul className="space-y-4 mb-8">
                {repairIssues.map((issue, index) => (
                  <li key={`repair-issue-${index}-${issue.slice(0, 10)}`} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-gray-700 text-lg">{issue}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                <div className="flex items-start">
                  <AlertTriangle className="w-6 h-6 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Why Act Early?</h3>
                    <p className="text-gray-700">
                      Early repairs can help prevent structural damage, internal water issues, and more expensive replacement work later on.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="bg-gray-50 p-8 rounded-xl">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Get Your Roof Inspected
                </h2>
                <p className="text-gray-600 mb-6">
                  If your roof needs attention, contact Dezire Metal Roofing for a free inspection and honest advice on the best repair solution.
                </p>
                <p className="text-gray-600 mb-8">
                  We assess the damage, explain your options clearly, and provide a detailed quote before any work begins.
                </p>

                <Link to="/book-inspection">
                  <Button className="w-full bg-primary hover:bg-accent text-white mb-4" data-testid="content-cta-btn">
                    Book a Free Inspection
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                
                <p className="text-center text-gray-500 text-sm">
                  Or call us directly: <a href="/call" className="text-primary font-semibold">0433 973 844</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <ServiceGallery service="roof-repairs" />

      {/* Related Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Related Services
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/services/metal-roof-installation" className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Metal Roof Installation</h3>
              <p className="text-gray-600 text-sm">New roof installations</p>
            </Link>
            <Link to="/services/roof-replacement" className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Roof Replacement</h3>
              <p className="text-gray-600 text-sm">Full roof replacement</p>
            </Link>
            <Link to="/services/emergency-repairs" className="bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-gray-900 mb-2">Emergency Repairs</h3>
              <p className="text-gray-600 text-sm">24/7 urgent repairs</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RoofRepairsPage;
